"use client";

// Browser localStorage stands in for the mobile app's on-device SharedPreferences
// store — dismissed suggestions, automation rules, smart folders, sender flags and
// cleanup history all live only on this device, same as the app.

import type { AutomationRule, CleanupHistoryEntry, SenderFlags, SmartFolder } from "./types";

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

const DISMISSED_KEY = "maila.dismissed_suggestions";
export const dismissedSuggestionsStore = {
  load(): Set<string> {
    return new Set(readJson<string[]>(DISMISSED_KEY, []));
  },
  dismiss(key: string): void {
    const set = dismissedSuggestionsStore.load();
    set.add(key);
    writeJson(DISMISSED_KEY, [...set]);
  },
};

const RULES_KEY = "maila.rules";
export const rulesStore = {
  load(): AutomationRule[] {
    return readJson<AutomationRule[]>(RULES_KEY, []);
  },
  save(rules: AutomationRule[]): void {
    writeJson(RULES_KEY, rules);
  },
};

const FOLDERS_KEY = "maila.smart_folders";
export const smartFoldersStore = {
  load(): SmartFolder[] | null {
    return readJson<SmartFolder[] | null>(FOLDERS_KEY, null);
  },
  save(folders: SmartFolder[]): void {
    writeJson(FOLDERS_KEY, folders);
  },
};

const SENDER_FLAGS_KEY = "maila.sender_flags";
export const senderFlagsStore = {
  load(): Record<string, SenderFlags> {
    return readJson<Record<string, SenderFlags>>(SENDER_FLAGS_KEY, {});
  },
  setFlag(email: string, flags: SenderFlags): void {
    const all = senderFlagsStore.load();
    all[email] = { ...all[email], ...flags };
    writeJson(SENDER_FLAGS_KEY, all);
  },
};

const HISTORY_KEY = "maila.cleanup_history";
export const cleanupHistoryStore = {
  load(): CleanupHistoryEntry[] {
    return readJson<CleanupHistoryEntry[]>(HISTORY_KEY, []);
  },
  add(entry: CleanupHistoryEntry): void {
    const current = cleanupHistoryStore.load();
    writeJson(HISTORY_KEY, [entry, ...current].slice(0, 50));
  },
};
